/* eslint-disable react/no-danger */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InputSwitch } from 'primereact/inputswitch';
import { classNames } from 'primereact/utils';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Paginator } from 'primereact/paginator';
import { Dropdown } from 'primereact/dropdown';
import moment from 'moment';

import './NotificationPopover.scss';
import { getInitials, renderMentionFormattedText } from '../../utils';
import EmptyImage from '../../assets/images/empty-table-bg.png';
import GearIcon from '../../assets/icons/gear-icon.svg';

const NotificationPopover = ({
  notifications,
  markAsRead,
  viewType = 'popover',
  closePopup,
  getOnlyUnread,
  loading,
  onPageChange,
  onTabChange,
  SaveActivaTab,
  onSortChange,
}: any) => {
  const rowsPerPage: any = 10;
  const navigate = useNavigate();

  const sortOptions = [
    { label: 'Newest First', value: 'desc' },
    { label: 'Oldest First', value: 'asc' },
  ];

  const [onlyUnread, setOnlyUnread] = useState<any>(false);
  const [activeFilter, setActiveFilter] = useState<any>('all');
  const [sortOrder, setSortOrder] = useState<any>('desc');

  const [allNotifications, setAllNotifications] = useState<any>(null);
  const [annotations, setAnnotations] = useState<any>(null);
  const [mentions, setMentions] = useState<any>(null);

  useEffect(() => {
    if (notifications) {
      setAllNotifications(notifications?.all_notifications);
      setAnnotations(notifications?.annotations);
      setMentions(notifications?.mentions);
    }
  }, [notifications]);

  useEffect(() => {
    if (activeFilter && typeof SaveActivaTab === 'function') {
      SaveActivaTab(activeFilter);
    }
  }, [activeFilter]);

  const onclickFilter = (filterType: any) => {
    setActiveFilter(filterType);
    onTabChange(filterType);
  };

  const getActiveNotifications = () => {
    if (activeFilter === 'mentions') return mentions?.notifications ?? [];
    if (activeFilter === 'annotations') return annotations?.notifications ?? [];
    return allNotifications?.notifications ?? [];
  };

  const currentList = getActiveNotifications();

  const activeNotifications = (() => {
    if (activeFilter === 'mentions') return mentions;
    if (activeFilter === 'annotations') return annotations;
    return allNotifications;
  })();

  return (
    <div className="notification-popover-inner">
      <div className="popover-header flex justify-content-between align-items-center mb-2">
        <div className="popover-title">Notifications</div>
        <div className="flex align-items-center gap-3">
          <span id="switch" className="toggle-label">Only show unread</span>
          <InputSwitch
            checked={onlyUnread}
            onChange={(e: any) => {
              if (e.value === true) {
                setOnlyUnread(true);
                getOnlyUnread(true);
              } else {
                setOnlyUnread(false);
                getOnlyUnread(false);
              }
            }}
            aria-labelledby="switch"
            className="common-toggle-btn"
          />
          {viewType === 'page' && (
            <>
              <Button type="button" label="Mark All as Read" className="ml-3" onClick={() => markAsRead('all')} />
              <Button
                style={{ width: '4px', alignSelf: 'flex-start', border: 'none' }}
                icon={() => <img src={GearIcon} alt="cross" />}
                className="p-button-text p-button-sm text-gray-600 hover:text-gray-800 close-banner-btn"
                onClick={() => { navigate('/notification-settings'); }}
                aria-label="Close"
              />
            </>
          )}
        </div>
      </div>

      <div className="notification-content-section">
        <div className="tab-filters">
          <div className={classNames(activeFilter === 'all' ? 'tab-filter-item active-filter-item' : 'tab-filter-item')} onClick={() => onclickFilter('all')}>
            <div className="tab-filter-text">All</div>
            <div className="tab-filter-count">{allNotifications?.total_items}</div>
          </div>
          <div className={classNames(activeFilter === 'mentions' ? 'tab-filter-item active-filter-item' : 'tab-filter-item')} onClick={() => onclickFilter('mentions')}>
            <div className="tab-filter-text">Mentions</div>
            <div className="tab-filter-count">{mentions?.total_items}</div>
          </div>
          <div className={classNames(activeFilter === 'annotations' ? 'tab-filter-item active-filter-item' : 'tab-filter-item')} onClick={() => onclickFilter('annotations')}>
            <div className="tab-filter-text">Annotations</div>
            <div className="tab-filter-count">{annotations?.total_items}</div>
          </div>
          {viewType === 'popover' && (
            <div>
              <Button
                style={{ height: '27px', width: '4px', alignSelf: 'flex-start', border: 'none' }}
                icon={() => <img src={GearIcon} alt="cross" />}
                className="p-button-text p-button-sm text-gray-600 hover:text-gray-800 close-banner-btn"
                onClick={() => { navigate('/notification-settings'); }}
                aria-label="Close"
              />
            </div>
          )}
          {viewType !== 'popover' && (
            <div className="tab-filters-sort flex align-items-center">
              <Dropdown
                value={sortOrder}
                options={sortOptions}
                onChange={(e: any) => {
                  setSortOrder(e.value);
                  if (typeof onSortChange === 'function') {
                    onSortChange(e.value);
                  }
                }}
                placeholder="Sort by"
                className="common-dropdown"
                style={{
                  marginBottom: '2px'
                }}
              />
            </div>
          )}
        </div>
        {viewType === 'popover' && loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              paddingTop: '90px',
              paddingBottom: '90px'
            }}
          >
            <ProgressSpinner />
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto notification-item-container">
            {currentList?.length === 0 ? (
              <div className="empty-container" style={{ marginTop: '50px', marginBottom: '50px' }}>
                <img src={EmptyImage} alt="no record" width="105" />
                <span className="empty-msg">You are all caught up</span>
              </div>
            ) : (
              <>
                {currentList?.map((n: any) => (
                  <div
                    key={n.id}
                    className={`notification-item cursor-pointer ${!n.is_read && 'notification-item-unread'} hover:bg-gray-50`}
                    onClick={() => markAsRead(n.id, n)}
                  >
                    {!n.is_read ? (
                      <div className="unread-icon" />
                    ) : (
                      <div className="read-icon" />
                    )}
                    <div className="flex-shrink-0 flex justify-center">
                      {n.action_initiator ? (
                        <Avatar
                          label={getInitials(n.fullName ? n.fullName : n.action_initiator)}
                          className="user-avatar"
                          shape="circle"
                          style={{ fontSize: '12px', fontWeight: '500' }}
                        />
                      ) : (
                        <Avatar
                          icon="pi pi-bell"
                          className="user-avatar"
                          shape="circle"
                        />
                      )}
                    </div>
                    <div className="notification-item-content flex-1">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: renderMentionFormattedText(n.notification_message, n.at_mentions ? n.at_mentions : {}, 'transparent')
                        }}
                        style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '18px' }}
                      />
                    </div>
                    <div className="notification-item-time">
                      {moment.utc(n.created_datetime).fromNow()}
                    </div>
                  </div>
                ))}

                {activeNotifications.total_items > rowsPerPage && viewType !== 'popover' && (
                  <Paginator
                    first={(activeNotifications.current_page - 1) * rowsPerPage}
                    rows={rowsPerPage}
                    totalRecords={activeNotifications.total_items}
                    onPageChange={(e: any) => onPageChange(e.page + 1)}
                    template="PrevPageLink PageLinks NextPageLink"
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>
      {viewType === 'popover' && (
        <div className="popover-footer">
          <div
            className="view-all-link"
            onClick={() => {
              closePopup();
              navigate('/all-notifications');
            }}
          >
            View All
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPopover;
